import { useCallback, useState, type Dispatch, type SetStateAction } from 'react';

import { useIsSSR } from './use-is-ssr.ts';

type SSRStateSnapshots<T> = {
  /** Snapshotted once on first client render; painted only after hydration. */
  client: () => T;
  /** Called on every server / hydration-pass render so it can match the document. */
  server: () => T;
};

/**
 * Writable state whose first paint matches the prerendered document, then the client
 * snapshot. After `setState`, `isSSR` no longer participates — same contract as `useState`.
 *
 * `server` is the `getServerSnapshot` equivalent: a repeatable read of what the HTML already
 * says. `client` is lazy, like `useState`'s initializer, and must not be what you paint while
 * still hydrating.
 */
export const useSSRState = <T>({
  client,
  server,
}: SSRStateSnapshots<T>): [T, Dispatch<SetStateAction<T>>] => {
  const isSSR = useIsSSR();
  const [clientSnapshot] = useState(() => client());
  const [override, setOverride] = useState<{ value: T }>();

  const value = override !== undefined ? override.value : isSSR ? server() : clientSnapshot;

  // Stable like `useState`'s dispatch, so callers can leave it out of dependency arrays.
  const setState = useCallback(
    (update: SetStateAction<T>) => {
      setOverride((prev) => {
        const current = prev !== undefined ? prev.value : clientSnapshot;
        return {
          value: typeof update === 'function' ? (update as (prev: T) => T)(current) : update,
        };
      });
    },
    [clientSnapshot],
  );

  return [value, setState];
};
